import { isValidColor } from 'helpers/colors';
import PropTypes from 'prop-types';
import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { formatCommas, formatMoney } from 'utils/utils';

export const COLORS = {
  Aetna: '#6E2B8C',
  Ambetter: '#C41879',
  'AmeriHealth Caritas': '#2ca02c',
  Anthem: '#0076BC',
  Ascension: '#9467bd',
  AvMed: '#F2D957',
  'BCBS AZ': '#e377c2',
  'BCBS IL': '#7f7f7f',
  'BCBS MI': '#bcbd22',
  'BCBS MT': '#17becf',
  'BCBS NC': '#aec7e8',
  'BCBS OK': '#ffbb78',
  'BCBS SC': '#98df8a',
  'BCBS TN': '#ff9896',
  'BCBS TX': '#c5b0d5',
  CareSource: '#80438A',
  'Christus Health': '#40185D',
  Cigna: '#0131F7',
  'Enrollment Prime': '#dbdb8d',
  'Health First': '#9edae5',
  'Highmark BCBS': '#393b79',
  Medica: '#5254a3',
  Moda: '#6b6ecf',
  Molina: '#009DA6',
  Oscar: '#5041F7',
  'Priority Health': '#8ca252',
  'Providence Health Plan': '#002379',
  'United Health Care': '#e7ba52',
  'United Health One': '#bd9e39',
  Other: '#cccccc'
};

export const HIDDEN_KEYS = ['Other'];

function rowTotal(row, colorKeys = Object.keys(COLORS)) {
  return colorKeys.reduce((s, k) => {
    if (HIDDEN_KEYS.includes(k)) return s;
    return s + (Number(row?.[k]) || 0);
  }, 0);
}

function SortedStackShape(props) {
  const { x, y, width, height, payload, colors } = props;
  if (!payload || width <= 0 || height <= 0) return null;
  const colorMap = colors || COLORS;
  const entries = Object.keys(colorMap)
    .map(k => ({
      key: k,
      value: Number(payload?.[k]) || 0,
      color: colorMap[k]
    }))
    .filter(e => e.value > 0 && !HIDDEN_KEYS.includes(e.key))
    .sort((a, b) => a.value - b.value);
  const total =
    Number(payload?.Total) || rowTotal(payload, Object.keys(colorMap));
  if (!total) return null;
  let acc = 0;
  return (
    <g>
      {entries.map(e => {
        const h = height * (e.value / total);
        const y0 = y + height - acc - h;
        acc += h;
        return (
          <rect
            key={e.key}
            x={x}
            y={y0}
            width={width}
            height={h}
            fill={e.color}
          />
        );
      })}
    </g>
  );
}

SortedStackShape.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  payload: PropTypes.shape({
    Total: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  colors: PropTypes.object
};

function SortedTooltip({ active, payload, label, colors }) {
  if (!active || !payload?.length) return null;
  const colorMap = colors || COLORS;
  const row = payload[0]?.payload ?? {};
  const items = Object.keys(colorMap)
    .map(k => ({ name: k, value: Number(row?.[k]) || 0, color: colorMap[k] }))
    .filter(it => it.value > 0 && !HIDDEN_KEYS.includes(it.name))
    .sort((a, b) => b.value - a.value);
  const total = Number(row?.Total) || items.reduce((s, it) => s + it.value, 0);

  return (
    <div style={{ background: 'white', border: '1px solid #ddd', padding: 10 }}>
      <div>
        <strong>{label}</strong> — Total: {formatMoney(total || 0)}
      </div>
      <ul style={{ margin: '6px 0 0 0', padding: 0, listStyle: 'none' }}>
        {items.map(it => (
          <li key={it.name} style={{ display: 'flex', gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                background: it.color,
                marginTop: 6
              }}
            />
            <span>
              {it.name}: <strong>{formatMoney(it.value)}</strong>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

SortedTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.any,
  colors: PropTypes.object
};

function CommissionsGraph({ data_for_graph, carrierColors }) {
  const resolvedColors = React.useMemo(() => {
    const merged = { ...COLORS };

    Object.entries(carrierColors || {}).forEach(([carrier, color]) => {
      if (isValidColor(color)) {
        merged[carrier] = color.trim();
      }
    });

    return merged;
  }, [carrierColors]);

  const maxTotal = React.useMemo(() => {
    if (!Array.isArray(data_for_graph)) return 0;
    return data_for_graph.reduce(
      (m, row) =>
        Math.max(
          m,
          Number(row?.Total) || rowTotal(row, Object.keys(resolvedColors))
        ),
      0
    );
  }, [data_for_graph, resolvedColors]);

  // Only show legend entries for carriers that have data
  const activeCarriers = React.useMemo(() => {
    if (!Array.isArray(data_for_graph)) return [];
    const totals = {};
    data_for_graph.forEach(row => {
      Object.keys(resolvedColors).forEach(carrier => {
        if (HIDDEN_KEYS.includes(carrier)) return;
        const val = Number(row?.[carrier]) || 0;
        if (val > 0) totals[carrier] = (totals[carrier] || 0) + val;
      });
    });
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [data_for_graph, resolvedColors]);

  return (
    <div className='flex flex-col gap-4 pb-4'>
      <ResponsiveContainer width={'100%'} height={600}>
        <BarChart
          data={data_for_graph}
          margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='name' />
          <YAxis
            width={90}
            domain={[0, maxTotal]}
            tickFormatter={v => `$${formatCommas(v)}`}
            tick={{ fontSize: 16 }}
          />
          <Tooltip content={<SortedTooltip colors={resolvedColors} />} />
          <Bar
            dataKey={row =>
              Number(row?.Total) || rowTotal(row, Object.keys(resolvedColors))
            }
            isAnimationActive={false}
            fill='transparent'
            shape={<SortedStackShape colors={resolvedColors} />}
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      {activeCarriers.length > 0 && (
        <div className='flex flex-wrap gap-x-4 gap-y-2 px-6'>
          {activeCarriers.map(carrier => (
            <div key={carrier} className='flex items-center gap-1.5'>
              <span
                className='inline-block w-3 h-3 rounded-sm shrink-0'
                style={{ backgroundColor: resolvedColors[carrier] }}
              />
              <span className='text-xs text-gray-600'>{carrier}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

CommissionsGraph.propTypes = {
  data_for_graph: PropTypes.array,
  carrierColors: PropTypes.object
};

const memoizedCommissionsGraph = React.memo(
  CommissionsGraph,
  (prevProps, nextProps) =>
    prevProps.data_for_graph === nextProps.data_for_graph &&
    prevProps.carrierColors === nextProps.carrierColors
);

export { memoizedCommissionsGraph as CommissionsGraph };
